/**
 * App React component (application root)
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


import React from 'react';
import {MuiThemeProvider, createMuiTheme} from '@material-ui/core/styles';
import {Provider} from 'react-redux';
import store from '../../store.js';
import MainView from './MainView';
import {MuiPickersUtilsProvider} from 'material-ui-pickers';
import DateFnsUtils from '@date-io/date-fns';

import '../../App.css';

import {restoreStateAction} from '../../redux/navigation/actions';
import Loader from "../base/Loader";
import MessageBar from "../base/MessageBar";
import MediaViewerOverlay from "../base/MediaViewerOverlay";
import {loginBox, verifyToken} from '../../operations/login';
import MessageDialog from "../base/MessageDialog";
import {addSocketListener} from "../../operations/cbox";
import {REST_SERVER_BASE_URL} from "../../config";
import io from "socket.io-client"
import CBoxView from "../presentation/CBoxView";
import {startStresstest} from "../../operations/testing";
import {activateTestMode} from "../../redux/testing/actions";
import {showInitialFilter} from "../../operations/listing";
import {editObject} from "../../operations/editing";
import {openControlPage, openAdminPage} from "../../operations/administration";
import TestMonitor from "../misc/TestMonitor";
import {extractURLParams} from "../../utils";
import {loadConfiguration} from "../../operations/configuration";


const themeMusos = createMuiTheme(
    {
        typography: {
            useNextVariants: true,
        },
        palette: {
            primary: {main: '#147c91'}, // #049bbf, #063446, #0a7dab, #147c91
            secondary: {main: '#1aa3bf'}, // 0dfbd0
            notification: {main: '#ff0000'}
        },
        overrides: {
            MuiNotchedOutline: {
                root: {
                    borderColor: "#949494"
                }
            }
        }
    });

const themePulsar = createMuiTheme(
    {
        typography: {
            useNextVariants: true,
        },
        palette: {
            primary: {main: '#083745'}, // #049bbf, #063446, #0a7dab, #147c91
            secondary: {main: '#1aa3bf'}, // 0dfbd0
            notification: {main: '#ff0000'}
        },
        overrides: {
            MuiNotchedOutline: {
                root: {
                    borderColor: "#949494"
                }
            }
        }
    });


/**
 * App is the root component of the application usually showing the main view
 */
class App extends React.Component {

    socket = null;

    constructor(props) {
        super(props);

        // register event for browser navigation (back button)
        window.onpopstate = event => {
            store.dispatch(restoreStateAction(event.state));
        };

        const params = extractURLParams();
        console.log("URL params", params);
        const cboxName = params["cbox"];
        const stresstest = params["stresstest"];

        if (cboxName) {
            this.state = {mode: "cbox"};
            const screenIndex = parseInt(params["screen"]) || 0;
            this.initCbox(cboxName, screenIndex);
        }
        else if (stresstest) {
            this.state = {mode: "stresstest"};
            this.initStresstest();
        }
        else {
            this.state = {mode: "normal"};
            this.initNormal(params);
        }
    }

    initCbox(cboxName, screenIndex) {

        console.log("cbox name:", cboxName, "screen:", screenIndex);
        loginBox(cboxName, screenIndex)(store.dispatch, store.getState);
        document.title = 'cbox-' + cboxName + '-' + screenIndex;

        // connect
        this.socket = io.connect(REST_SERVER_BASE_URL);

        this.socket.on('connect', () => {
            console.log("socket connected with " + REST_SERVER_BASE_URL);
            this.socket.emit("joinbox", {name: cboxName});
        });

        this.socket.on('connect_error', (error) => {
            console.log("error connecting socket to " + REST_SERVER_BASE_URL + ":", error);
        });

        this.socket.on('connect_timeout', (timeout) => {
            console.log("timeout connecting socket to " + REST_SERVER_BASE_URL + ":", timeout);
        });

        this.socket.on('disconnect', () => {
            console.log("socket disconnected from " + REST_SERVER_BASE_URL);
        });

        this.socket.on('reconnect', () => {
            // 'connect' is also fired on 'reconnect',
            // so there is nothing to do here (right now)
        });

        store.dispatch(addSocketListener(this.socket));
    }

    async initNormal(params) {

        // verify web token if already existing
        if (typeof(Storage) !== "undefined") {
            const accessToken = sessionStorage.getItem('token');
            if (accessToken) {
                if (await verifyToken(accessToken)(store.dispatch, store.getState)) {
                    const objectId = params["object"];
                    if (objectId) {
                        await editObject(objectId)(store.dispatch, store.getState);
                    }
                    else if (params["page"] === "control") {
                        await openControlPage()(store.dispatch, store.getState);
                    }
                    else if (params["page"] === "admin") {
                        await openAdminPage()(store.dispatch, store.getState);
                    }
                    else {
                        await showInitialFilter()(store.dispatch, store.getState);
                    }
                }
            }
        }

        // TODO: As long as configuration is not loaded, do not render / display any logo
        await loadConfiguration()(store.dispatch, store.getState);
        console.log("theme: " + store.getState().configuration.active.theme);
        const appTitle = store.getState().configuration.active.apptitle;       
        if (appTitle) document.title = appTitle;
    }

    async initStresstest() {

        this.setState({mode: "stresstest"});
        await store.dispatch(activateTestMode());

        // verify web token if already existing
        if (typeof(Storage) !== "undefined") {
            const accessToken = sessionStorage.getItem('token');
            if (accessToken) {
                await verifyToken(accessToken)(store.dispatch, store.getState);
            }
        }

        startStresstest()(store.dispatch, store.getState);
    }

    render() {
        if (this.state.mode === "cbox") {

            const onAlive = (active, activity) => {
                if (this.socket) {
                    this.socket.emit("alive", {active: active, activity: activity});
                }
            }

            const onNotifyEvent = (eventId, sender, data = {}) => {
                if (this.socket) {
                    this.socket.emit("notifyevent", {eventid: eventId, sender: sender, ...data});
                }
            }
                
            return (
                <MuiThemeProvider theme={store.getState().configuration.active.theme === "pulsar" ? themePulsar : themeMusos}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Provider store={store}>
                            <CBoxView onAlive={onAlive} onNotifyEvent={onNotifyEvent}/>
                        </Provider>
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
            );
        }
        else if (this.state.mode === "stresstest") {
            return (
                <MuiThemeProvider theme={store.getState().configuration.active.theme === "pulsar" ? themePulsar : themeMusos}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Provider store={store}>
                            <div>
                                <TestMonitor/>
                                <div>
                                    <Loader/>
                                    <MessageBar/>
                                    <MessageDialog/>
                                    <MediaViewerOverlay/>
                                </div>
                            </div>
                        </Provider>
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
            );
        }
        else {
            return (
                <MuiThemeProvider theme={store.getState().configuration.active.theme === "pulsar" ? themePulsar : themeMusos}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Provider store={store}>
                            <div>
                                <div className="App">
                                    <MainView/>
                                </div>
                                <div>
                                    <Loader/>
                                    <MessageBar/>
                                    <MessageDialog/>
                                    <MediaViewerOverlay/>
                                </div>
                            </div>
                        </Provider>
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
            );
        }
    }
}

export default App;

